/**
 * SectionContextMenu — Overflow "..." menu on section rows with
 * level-aware actions: move into container, move to root, add child,
 * duplicate, and delete.
 */

import { MoreHorizontal, ArrowDownToLine, ArrowUpFromLine, Plus, Copy, Trash2, MousePointerClick } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  isContainerType,
  findSectionByEditorId,
  MAX_CONTAINER_DEPTH,
  WIDGET_REGISTRY,
} from '@zaviago/storefront-config'
import type { EditorSection } from './usePageSections'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SectionContextMenuProps {
  section: EditorSection
  depth: number
  isContainer: boolean
  sections: EditorSection[]
  onMoveToContainer: (editorId: string, containerId: string) => void
  onMoveToRoot: (editorId: string) => void
  onAddChild: (parentEditorId: string, depth: number) => void
  onDuplicate: (editorId: string) => void
  onDelete: (editorId: string) => void
  onSelectFirstBlock?: (editorId: string) => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Humanize a type key like "product-grid" → "Product Grid" */
function getLabel(section: EditorSection): string {
  const widget = WIDGET_REGISTRY[section.type]
  return (
    widget?.schema.label ??
    section.type.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

/** Check if `candidateId` is a descendant of `ancestorId` in the tree. */
function isDescendantOf(
  sections: EditorSection[],
  ancestorId: string,
  candidateId: string,
): boolean {
  const ancestor = findSectionByEditorId(sections, ancestorId)
  if (!ancestor) return false
  const ancestorSection = ancestor.section as EditorSection
  if (!ancestorSection.children?.length) return false

  function walk(items: EditorSection[]): boolean {
    for (const s of items) {
      if (s._editorId === candidateId) return true
      if (s.children && walk(s.children)) return true
    }
    return false
  }
  return walk(ancestorSection.children)
}

/** Get the maximum nesting depth below a section (0 for leaf). */
function getMaxDepthBelow(section: EditorSection): number {
  if (!section.children?.length) return 0
  let max = 0
  for (const child of section.children) {
    max = Math.max(max, 1 + getMaxDepthBelow(child))
  }
  return max
}

interface ContainerTarget {
  editorId: string
  label: string
  depth: number
}

/** Collect all containers that are valid move targets. */
function getAvailableContainers(
  sections: EditorSection[],
  excludeEditorId: string,
  movingSection: EditorSection,
): ContainerTarget[] {
  const targets: ContainerTarget[] = []
  const movingDepthBelow = getMaxDepthBelow(movingSection)

  function walk(items: EditorSection[], depth: number) {
    for (const s of items) {
      if (
        isContainerType(s.type) &&
        s._editorId !== excludeEditorId &&
        !isDescendantOf(sections, excludeEditorId, s._editorId) &&
        depth + 1 + movingDepthBelow < MAX_CONTAINER_DEPTH
      ) {
        targets.push({ editorId: s._editorId, label: getLabel(s), depth })
      }
      if (s.children?.length) {
        walk(s.children, depth + 1)
      }
    }
  }
  walk(sections, 0)
  return targets
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SectionContextMenu({
  section,
  depth,
  isContainer,
  sections,
  onMoveToContainer,
  onMoveToRoot,
  onAddChild,
  onDuplicate,
  onDelete,
  onSelectFirstBlock,
}: SectionContextMenuProps) {
  const isChild = depth > 0
  const containers = getAvailableContainers(sections, section._editorId, section)
  const showMoveInto = !isChild && containers.length > 0

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex-shrink-0 rounded-md p-1 text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-500 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-500"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          title="Section actions"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-52 rounded-sm shadow-apple-sm" align="end">
        {/* Container: Add section inside */}
        {isContainer && (
          <DropdownMenuItem
            onClick={() => onAddChild(section._editorId, depth + 1)}
          >
            <Plus className="h-4 w-4" />
            Add section inside
          </DropdownMenuItem>
        )}

        {/* Child: Move to root */}
        {isChild && (
          <DropdownMenuItem
            onClick={() => onMoveToRoot(section._editorId)}
          >
            <ArrowUpFromLine className="h-4 w-4" />
            Move to root level
          </DropdownMenuItem>
        )}

        {/* Root: Move into container (submenu) */}
        {showMoveInto && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ArrowDownToLine className="h-4 w-4" />
              Move into container...
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="w-48">
                {containers.map((c) => (
                  <DropdownMenuItem
                    key={c.editorId}
                    onClick={() => onMoveToContainer(section._editorId, c.editorId)}
                  >
                    {'  '.repeat(c.depth)}{c.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}

        {/* Select first block (block-enabled sections) */}
        {(section.blocks?.length ?? 0) > 0 && onSelectFirstBlock && (
          <DropdownMenuItem onClick={() => onSelectFirstBlock(section._editorId)}>
            <MousePointerClick className="h-4 w-4" />
            Select first block
          </DropdownMenuItem>
        )}

        {(isContainer || isChild || showMoveInto || (section.blocks?.length ?? 0) > 0) && <DropdownMenuSeparator />}

        {/* Duplicate */}
        <DropdownMenuItem onClick={() => onDuplicate(section._editorId)}>
          <Copy className="h-4 w-4" />
          Duplicate
        </DropdownMenuItem>

        {/* Delete */}
        <DropdownMenuItem
          onClick={() => onDelete(section._editorId)}
          className="text-red-600 focus:bg-red-50 focus:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
